import { baseApi } from "@/shared/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import { isProjectRemovalRequestDetails } from "@/features/self/pending/types/pending.types";
import type { ProjectRemovalRequest, RequestRemovalPayload } from "../types";

interface DecisionArgs {
  id: number;
  requestId: number;
}

interface RejectArgs extends DecisionArgs {
  reason?: string;
}

function removePendingRemovalItem(requestId: number) {
  return pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
    const idx = draft.items.findIndex(
      (item) =>
        item.action_required === "approve_project_removal" &&
        isProjectRemovalRequestDetails(item.details) &&
        item.details.request_id === requestId,
    );
    if (idx !== -1) {
      draft.items.splice(idx, 1);
      draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
    }
  });
}

function decrementPendingCount() {
  return authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
    if (draft.count > 0) draft.count -= 1;
  });
}

export const projectRemovalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    requestProjectRemoval: builder.mutation<ProjectRemovalRequest, RequestRemovalPayload>({
      query: ({ id, action_type, reason }) => ({
        url: `/api/projects/${id}/request-removal/`,
        method: "POST",
        body: { action_type, reason },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Projects", id: "LIST" },
        { type: "Projects", id },
        "Projects",
        { type: "Organization" as const, id: "ME" },
        "PoolBalances",
        { type: "Budget" as const, id: "SUMMARY" },
        { type: "Budget" as const, id: "PROJECT_TABLE" },
        { type: "IncomeRecords" as const, id: "LIST" },
        { type: "AuditEntries" as const, id: "PROJECT_LIST" },
        { type: "ProjectReports" as const, id: "LIST" },
        { type: "Tasks" as const, id: "LIST" },
        { type: "TaskBoard" as const, id: "LIST" },
        { type: "TaskGoals" as const, id: "LIST" },
        "ProjectMembers",
        "ProjectPublications",
        "ProjectEvents",
        "Sroi",
        "PendingActions",
      ],
    }),
    approveProjectRemoval: builder.mutation<ProjectRemovalRequest, DecisionArgs>({
      query: ({ id }) => ({
        url: `/api/projects/${id}/approve-removal/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Projects", id: "LIST" },
        { type: "Projects", id },
        "Projects",
        { type: "Organization" as const, id: "ME" },
        "PoolBalances",
        { type: "Budget" as const, id: "SUMMARY" },
        { type: "Budget" as const, id: "PROJECT_TABLE" },
        { type: "IncomeRecords" as const, id: "LIST" },
        { type: "AuditEntries" as const, id: "PROJECT_LIST" },
        { type: "ProjectReports" as const, id: "LIST" },
        { type: "Tasks" as const, id: "LIST" },
        { type: "TaskBoard" as const, id: "LIST" },
        { type: "TaskGoals" as const, id: "LIST" },
        "ProjectMembers",
        "ProjectPublications",
        "ProjectEvents",
        "Sroi",
        "PendingActions",
      ],
      async onQueryStarted({ requestId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(removePendingRemovalItem(requestId));
        const pendingCountPatch = dispatch(decrementPendingCount());
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
    rejectProjectRemoval: builder.mutation<ProjectRemovalRequest, RejectArgs>({
      query: ({ id, reason }) => ({
        url: `/api/projects/${id}/reject-removal/`,
        method: "POST",
        body: { reason },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Projects", id },
        "PendingActions",
      ],
      async onQueryStarted({ requestId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(removePendingRemovalItem(requestId));
        const pendingCountPatch = dispatch(decrementPendingCount());
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
    cancelProjectRemoval: builder.mutation<ProjectRemovalRequest, DecisionArgs>({
      query: ({ id }) => ({
        url: `/api/projects/${id}/cancel-removal/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Projects", id },
        "PendingActions",
      ],
    }),
  }),
});

export const {
  useRequestProjectRemovalMutation,
  useApproveProjectRemovalMutation,
  useRejectProjectRemovalMutation,
  useCancelProjectRemovalMutation,
} = projectRemovalApi;
